import { FunctionComponent, MutableRefObject } from 'react'
import { css, cx } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import type {
  OnlineOperationalMenuItems,
  OnlineOperationalModifierGroup,
  OnlineOperationalModifierOptions,
} from '@/libs/helpers/adapters'
import { useMenuItem } from '@/libs/helpers/hooks/useMenuItem'
import { Grid } from '@/tbui'
import { textStyles } from '@/tbui/Text/styles'
import ModifierOption from './ModifierOption'

const StyledModifierGroup = styled.div<{ hasError?: boolean }>`
  background: ${(props) =>
    props.hasError ? props.theme.palette.ERROR_LIGHT : props.theme.palette.WHITE};
  padding: 1rem;
  display: grid;
`

const groupNameStyles = (theme: Theme): string => css`
  ${textStyles(theme).heading.three};
  text-transform: capitalize;

  &.has-error {
    color: ${theme.palette.ERROR_TEXT};
  }
`

const messageStyles = (theme: Theme): string => css`
  ${textStyles(theme).text.five};
  text-transform: uppercase;
  margin: 5px 0 0 0;
  color: ${theme.palette.GRAY_3};

  &.has-error {
    color: ${theme.palette.ERROR_TEXT};
  }
`

interface ModifierGroupProps {
  modifierGroup: OnlineOperationalModifierGroup
  modifierGroupDOMRefs: MutableRefObject<Record<string, HTMLDivElement>>
  calculatePrice: () => void
  modifierOptions: OnlineOperationalModifierOptions
  menuItems: OnlineOperationalMenuItems
}

const ModifierGroup: FunctionComponent<ModifierGroupProps> = ({
  modifierGroup,
  modifierGroupDOMRefs,
  calculatePrice,
  modifierOptions,
  menuItems,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const getRequiredMessageString = (): string => {
    const hasMin = Boolean(modifierGroup.selectionMin)
    const hasMax = Boolean(modifierGroup.selectionMax)

    if (hasMin || hasMax) {
      return t(`menu_item.modifiers.validation.${hasMin}_${hasMax}`, {
        count: modifierGroup.selectionMax || modifierGroup.selectionMin,
        min: modifierGroup.selectionMin,
        max: modifierGroup.selectionMax,
      })
    }
    return ''
  }

  const {
    validateModifiers,
    currentErrors,
    openedNestedModifier,
    currentModifiers,
    shouldDisplayErrors,
  } = useMenuItem()

  const hasError = (() => {
    if (!shouldDisplayErrors) {
      return false
    }
    const node = currentErrors[modifierGroup.XRefID]
    return node != null && node.error !== 'NESTED_MODIFIER'
  })()

  const titleColor = hasError ? 'ERROR' : 'TEXT'
  const isDisabled = ((): boolean => {
    if (modifierGroup.selectionMax > 0) {
      let quantitySum = 0
      currentModifiers
        .filter((modifier) => modifier.modifierGroupXRefID === modifierGroup.XRefID)
        .forEach((value) => (quantitySum += Number(value.quantity ?? 0)))
      return quantitySum >= modifierGroup.selectionMax
    }
    return false
  })()

  const modifiersUpdated = (): void => {
    validateModifiers()
    calculatePrice()
  }

  const renderModifierOptions = (): JSX.Element[] => {
    const modifierOptionsToRender: JSX.Element[] = []
    for (const modifierOptionRef of modifierGroup.modifierOptionRefs) {
      let selectionId = `${modifierGroup.XRefID}_${modifierOptions[modifierOptionRef.id].XRefID}`
      if (openedNestedModifier != null) {
        selectionId = `${openedNestedModifier.itemID}_${selectionId}`
      }
      let hasNestedModifiers = false
      if (modifierOptions[modifierOptionRef.id].menuItemXRefID != null) {
        const matchedMenuItem =
          menuItems[modifierOptions[modifierOptionRef.id].menuItemXRefID as string]
        hasNestedModifiers = matchedMenuItem?.modifierGroupRefs.length > 0
      }
      modifierOptionsToRender.push(
        <ModifierOption
          key={selectionId}
          selectionId={selectionId}
          modifierOption={modifierOptions[modifierOptionRef.id]}
          isDisabled={isDisabled}
          modifiersUpdated={modifiersUpdated}
          selectionMax={modifierGroup.selectionMax}
          modifierGroupID={modifierGroup.XRefID}
          hasNestedModifiers={hasNestedModifiers}
          modifierGroupDOMRefs={modifierGroupDOMRefs}
        />
      )
    }
    return modifierOptionsToRender
  }

  return (
    <StyledModifierGroup
      hasError={hasError}
      data-test="modifier-group-section"
      ref={(modGroup) => {
        // only required modifier groups can be scrolled to
        if (modifierGroup.selectionMin > 0 && modGroup != null) {
          // eslint-disable-next-line no-param-reassign
          modifierGroupDOMRefs.current[modifierGroup.XRefID] = modGroup
        }
      }}
    >
      <Grid>
        <h3
          className={cx(groupNameStyles(theme), `${hasError ? 'has-error' : ''}`)}
          color={titleColor}
          data-test={`modifier-group-${modifierGroup.XRefID}-title`}
        >
          {modifierGroup.label}
        </h3>
        <h5
          color={titleColor}
          className={cx(messageStyles(theme), `${hasError ? 'has-error' : ''}`)}
          data-test={`modifier-group-${modifierGroup.XRefID}-subtitle`}
        >
          {getRequiredMessageString()}
        </h5>
      </Grid>
      {renderModifierOptions()}
    </StyledModifierGroup>
  )
}

export default ModifierGroup
